import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

const STARTUP_RETRY_DELAY_MS = 750;

const resolveContainedMediaRect = (mediaEl) => {
    if (!mediaEl || typeof mediaEl.getBoundingClientRect !== 'function') {
        return null;
    }

    const rect = mediaEl.getBoundingClientRect();
    const boxWidth = rect.width;
    const boxHeight = rect.height;

    if (!boxWidth || !boxHeight) {
        return null;
    }

    const intrinsicWidth = Number(mediaEl.videoWidth || mediaEl.clientWidth || boxWidth);
    const intrinsicHeight = Number(mediaEl.videoHeight || mediaEl.clientHeight || boxHeight);

    if (!intrinsicWidth || !intrinsicHeight) {
        return rect;
    }

    const mediaAspect = intrinsicWidth / intrinsicHeight;
    const boxAspect = boxWidth / boxHeight;

    let visibleWidth = boxWidth;
    let visibleHeight = boxHeight;
    let offsetX = 0;
    let offsetY = 0;

    if (boxAspect > mediaAspect) {
        visibleWidth = boxHeight * mediaAspect;
        offsetX = (boxWidth - visibleWidth) / 2;
    } else if (boxAspect < mediaAspect) {
        visibleHeight = boxWidth / mediaAspect;
        offsetY = (boxHeight - visibleHeight) / 2;
    }

    return {
        left: rect.left + offsetX,
        top: rect.top + offsetY,
        width: visibleWidth,
        height: visibleHeight,
        right: rect.left + offsetX + visibleWidth,
        bottom: rect.top + offsetY + visibleHeight,
    };
};

/**
 * Fetch a same-origin URL while preserving the Laravel session cookie.
 */
const fetchWithSession = (url, options = {}) =>
    fetch(url, {
        credentials: 'same-origin',
        ...options,
    });

/**
 * Read an error payload from a bootstrap response and normalize it into an Error.
 */
const buildResponseError = async (response, fallbackMessage) => {
    try {
        const payload = await response.clone().json();
        if (payload && typeof payload.message === 'string' && payload.message.trim() !== '') {
            return new Error(payload.message);
        }
    } catch (error) {
        // Ignore JSON parsing failures and fall back to the generic message.
    }

    return new Error(fallbackMessage);
};

/**
 * Build the optional authorization headers used by side-channel requests.
 */
const buildAuthHeaders = (authToken) =>
    authToken
        ? {
              Authorization: `Bearer ${authToken}`,
          }
        : undefined;

/**
 * Fire a lightweight same-origin request for player side-channel endpoints.
 */
const fetchWithAuth = (url, authToken, debugLabel) => {
    if (!url) {
        return Promise.resolve();
    }

    return fetch(url, {
        method: 'GET',
        headers: buildAuthHeaders(authToken),
        credentials: 'same-origin',
    }).catch((error) => {
        console.warn(`[MediaMtxWebRtcPlayer] ${debugLabel ?? 'Request'} failed`, error);
    });
};

/**
 * Append the force_restart flag expected by the preview bootstrap endpoint.
 */
const withForceRestart = (url) => {
    if (!url) {
        return '';
    }

    return `${url}${url.includes('?') ? '&' : '?'}force_restart=1`;
};

/**
 * Signed share bootstrap URLs must be called exactly as signed, without extra query params.
 */
const isSignedBootstrapUrl = (url) => {
    if (!url) {
        return false;
    }

    const parsedUrl = new URL(url, window.location.href);

    return parsedUrl.searchParams.has('signature') && parsedUrl.searchParams.has('expires');
};

/**
 * Resolve a WHEP endpoint from the configured player base URL.
 */
const resolveWhepUrl = (sourceUrl) => {
    if (!sourceUrl) {
        return '';
    }

    return sourceUrl.endsWith('/whep')
        ? sourceUrl
        : `${sourceUrl.replace(/\/+$/, '')}/whep`;
};

/**
 * Normalize session URLs back onto the same origin served by Laravel and Nginx.
 */
const normalizeSessionUrl = (locationHeader, whepUrl) => {
    const locationUrl = new URL(locationHeader, whepUrl);
    const playerUrl = new URL(whepUrl, window.location.href);

    if (locationUrl.origin === playerUrl.origin) {
        return locationUrl.toString();
    }

    return `${playerUrl.origin}${locationUrl.pathname}${locationUrl.search}`;
};

/**
 * Parse ICE server definitions returned by MediaMTX in the Link header.
 */
const parseIceServers = (linkHeader) => {
    if (!linkHeader) {
        return [];
    }

    return linkHeader
        .split(/,\s*(?=<)/)
        .map((entry) => {
            const match = entry.match(
                /^<(.+?)>; rel="ice-server"(; username="(.*?)"; credential="(.*?)"; credential-type="password")?/i
            );

            if (!match) {
                return null;
            }

            const iceServer = {
                urls: [match[1]],
            };

            if (match[3] !== undefined) {
                iceServer.username = JSON.parse(`"${match[3]}"`);
                iceServer.credential = JSON.parse(`"${match[4]}"`);
                iceServer.credentialType = 'password';
            }

            return iceServer;
        })
        .filter(Boolean);
};

/**
 * Extract the ICE credentials and media lines needed by trickle ICE PATCH calls.
 */
const parseLocalDescription = (sdp) => {
    const metadata = {
        iceUfrag: '',
        icePwd: '',
        mediaLines: [],
    };

    (sdp || '').split('\r\n').forEach((line) => {
        if (line.startsWith('m=')) {
            metadata.mediaLines.push(line.slice(2));
            return;
        }

        if (!metadata.iceUfrag && line.startsWith('a=ice-ufrag:')) {
            metadata.iceUfrag = line.slice('a=ice-ufrag:'.length);
            return;
        }

        if (!metadata.icePwd && line.startsWith('a=ice-pwd:')) {
            metadata.icePwd = line.slice('a=ice-pwd:'.length);
        }
    });

    return metadata;
};

/**
 * Build the SDP fragment required by WHEP PATCH candidate updates.
 */
const buildCandidateFragment = (localDescription, candidates) => {
    const groupedCandidates = {};

    candidates.forEach((candidate) => {
        const mediaIndex = candidate.sdpMLineIndex ?? 0;
        if (!groupedCandidates[mediaIndex]) {
            groupedCandidates[mediaIndex] = [];
        }
        groupedCandidates[mediaIndex].push(candidate);
    });

    let fragment = `a=ice-ufrag:${localDescription.iceUfrag}\r\n`;
    fragment += `a=ice-pwd:${localDescription.icePwd}\r\n`;

    localDescription.mediaLines.forEach((mediaLine, mediaIndex) => {
        const mediaCandidates = groupedCandidates[mediaIndex] || [];
        if (!mediaCandidates.length) {
            return;
        }

        fragment += `m=${mediaLine}\r\n`;
        fragment += `a=mid:${mediaIndex}\r\n`;
        mediaCandidates.forEach((candidate) => {
            fragment += `a=${candidate.candidate}\r\n`;
        });
    });

    return fragment;
};

/**
 * Render a same-origin MediaMTX WebRTC player that authenticates through Laravel cookies.
 */
const MediaMtxWebRtcPlayer = ({
    playerId,
    className = 'video-js vjs-default-skin',
    wrapperClassName = '',
    sourceUrl,
    controls = true,
    autoplay = false,
    muted = false,
    loop = false,
    preload = 'auto',
    fluid = true,
    height,
    width,
    live = false,
    playsInline = true,
    allowReload = false,
    showReloadButton = true,
    reloadButtonClassName = 'btn btn-sm btn-rose mt-3',
    reloadButtonText = 'Reload video',
    reloadOnError = true,
    reloadLimit = 6,
    reloadDelayMs = 2000,
    onlineStatusUrl,
    onlineStatusImmediate = true,
    onlineStatusInterval = 0,
    onlineStatusInitialDelay = 0,
    keepAliveUrl,
    keepAliveImmediate = false,
    keepAliveInterval = 0,
    authToken,
    debug = false,
    gridColumns = 0,
    gridRows = 0,
    gridDebug = false,
    gridOverlayClassName = 'live-video-player__grid-overlay',
    gridOverlayColor = 'rgba(255, 0, 0, 0.25)',
    onGridCellClick,
    overlayActions = [],
    overlayVisible = false,
    overlayClassName = '',
    footerActions = [],
    onReady,
    onError,
}) => {
    const rootRef = useRef(null);
    const videoNodeRef = useRef(null);
    const peerConnectionRef = useRef(null);
    const sessionUrlRef = useRef(null);
    const queuedCandidatesRef = useRef([]);
    const localDescriptionRef = useRef(null);
    const retryTimerRef = useRef(null);
    const requestTimersRef = useRef([]);
    const reloadCountRef = useRef(0);
    const [reloadNonce, setReloadNonce] = useState(0);
    const [errorMessage, setErrorMessage] = useState('');
    const [isBrowserFullscreen, setIsBrowserFullscreen] = useState(false);
    const [gridOverlayBounds, setGridOverlayBounds] = useState(null);

    const resolvedId = useMemo(() => playerId || `mediamtx-webrtc-${Math.random().toString(36).slice(2)}`, [playerId]);
    const rootClassName = useMemo(
        () => ['live-video-player', wrapperClassName, isBrowserFullscreen ? 'live-video-player--fullscreen' : '']
            .filter(Boolean)
            .join(' '),
        [isBrowserFullscreen, wrapperClassName]
    );
    const resolvedWhepUrl = useMemo(() => resolveWhepUrl(sourceUrl), [sourceUrl]);
    const resolvedPreload = useMemo(() => {
        if (typeof preload === 'boolean') {
            return preload ? 'auto' : 'none';
        }
        return preload ?? 'auto';
    }, [preload]);
    const shouldAutoPlay = useMemo(() => Boolean(autoplay || live || muted), [autoplay, live, muted]);
    const gridEnabled = useMemo(() => gridColumns > 0 && gridRows > 0, [gridColumns, gridRows]);
    const bootstrapUrl = useMemo(() => {
        if (!onlineStatusUrl) {
            return '';
        }

        return isSignedBootstrapUrl(onlineStatusUrl) ? onlineStatusUrl : withForceRestart(onlineStatusUrl);
    }, [onlineStatusUrl]);
    const normalizedOverlayActions = useMemo(
        () => (Array.isArray(overlayActions) ? overlayActions.filter(Boolean) : []),
        [overlayActions]
    );
    const normalizedFooterActions = useMemo(
        () => (Array.isArray(footerActions) ? footerActions.filter(Boolean) : []),
        [footerActions]
    );
    const actionsContainerClassName = useMemo(() => {
        const classes = ['live-video-player__actions'];
        if (overlayVisible) {
            classes.push('live-video-player__actions--active');
        }
        if (overlayClassName) {
            classes.push(overlayClassName);
        }
        return classes.join(' ');
    }, [overlayClassName, overlayVisible]);

    /**
     * Clear every scheduled timeout or interval created by the player.
     */
    const clearScheduledRequests = useCallback(() => {
        requestTimersRef.current.forEach(({ id, repeating }) => {
            if (repeating) {
                clearInterval(id);
            } else {
                clearTimeout(id);
            }
        });
        requestTimersRef.current = [];
    }, []);

    /**
     * Delay follow-up work long enough for a preview-start command to spin up MediaMTX.
     */
    const waitFor = useCallback((delayMs) => new Promise((resolve) => window.setTimeout(resolve, delayMs)), []);

    /**
     * Queue a one-shot or repeating side-channel request.
     */
    const scheduleRequest = useCallback((callback, delay, repeating = false) => {
        if (typeof callback !== 'function' || delay === null || delay === undefined) {
            return;
        }

        const id = repeating ? setInterval(callback, delay) : setTimeout(callback, delay);
        requestTimersRef.current.push({ id, repeating });
    }, []);

    /**
     * Tear down the active WebRTC session and detach the media stream.
     */
    const cleanupConnection = useCallback((deleteRemoteSession = true) => {
        const peerConnection = peerConnectionRef.current;
        peerConnectionRef.current = null;

        if (peerConnection) {
            peerConnection.ontrack = null;
            peerConnection.onicecandidate = null;
            peerConnection.onconnectionstatechange = null;
            peerConnection.close();
        }

        const sessionUrl = sessionUrlRef.current;
        sessionUrlRef.current = null;
        queuedCandidatesRef.current = [];
        localDescriptionRef.current = null;

        if (deleteRemoteSession && sessionUrl) {
            fetchWithSession(sessionUrl, { method: 'DELETE' }).catch(() => {});
        }

        const videoNode = videoNodeRef.current;
        if (videoNode && videoNode.srcObject) {
            const mediaStream = videoNode.srcObject;
            mediaStream.getTracks().forEach((track) => track.stop());
            videoNode.srcObject = null;
        }
    }, []);

    /**
     * Send local ICE candidates to the server after the WHEP session is created.
     */
    const sendLocalCandidates = useCallback(async (candidates) => {
        const sessionUrl = sessionUrlRef.current;
        const localDescription = localDescriptionRef.current;

        if (!sessionUrl || !localDescription || !candidates.length) {
            return;
        }

        const response = await fetchWithSession(sessionUrl, {
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/trickle-ice-sdpfrag',
                'If-Match': '*',
            },
            body: buildCandidateFragment(localDescription, candidates),
        });

        if (response.status !== 204) {
            throw new Error(`Unexpected WHEP candidate response: ${response.status}`);
        }
    }, []);

    /**
     * Force the Laravel preview bootstrap endpoint before a WebRTC connection attempt.
     */
    const bootstrapLivePreview = useCallback(async () => {
        if (!live || !bootstrapUrl) {
            return;
        }

        const response = await fetchWithSession(bootstrapUrl, {
            method: 'GET',
            headers: buildAuthHeaders(authToken),
        });

        if (!response.ok) {
            throw await buildResponseError(
                response,
                `Unexpected online status response: ${response.status}`
            );
        }

        await waitFor(STARTUP_RETRY_DELAY_MS);
    }, [authToken, bootstrapUrl, live, waitFor]);

    /**
     * Restart the player when the connection fails and retries are still allowed.
     */
    const handlePlaybackError = useCallback((error) => {
        const message = error instanceof Error ? error.message : String(error);
        setErrorMessage(message);
        onError?.(error instanceof Error ? error : new Error(message));

        if (!reloadOnError || reloadCountRef.current >= reloadLimit) {
            return;
        }

        reloadCountRef.current += 1;

        if (debug) {
            console.debug('[MediaMtxWebRtcPlayer] Scheduling reconnect', reloadCountRef.current, message);
        }

        if (retryTimerRef.current) {
            clearTimeout(retryTimerRef.current);
        }

        retryTimerRef.current = window.setTimeout(() => {
            setReloadNonce((value) => value + 1);
        }, reloadDelayMs);
    }, [debug, onError, reloadDelayMs, reloadLimit, reloadOnError]);

    useEffect(() => {
        if (!resolvedWhepUrl) {
            return undefined;
        }

        let cancelled = false;

        const startPlayback = async () => {
            try {
                cleanupConnection(false);
                setErrorMessage('');
                if (live && onlineStatusUrl) {
                    await bootstrapLivePreview();
                }

                let lastError = null;

                for (let attempt = 0; attempt < 2; attempt += 1) {
                    const optionsResponse = await fetchWithSession(resolvedWhepUrl, {
                        method: 'OPTIONS',
                    });
                    const iceServers = parseIceServers(optionsResponse.headers.get('Link'));

                    const peerConnection = new RTCPeerConnection({
                        iceServers,
                        sdpSemantics: 'unified-plan',
                    });
                    peerConnectionRef.current = peerConnection;

                    const mediaStream = new MediaStream();
                    const videoNode = videoNodeRef.current;
                    if (videoNode) {
                        videoNode.srcObject = mediaStream;
                    }

                    peerConnection.addTransceiver('video', { direction: 'recvonly' });
                    peerConnection.addTransceiver('audio', { direction: 'recvonly' });
                    peerConnection.createDataChannel('mediamtx');

                    peerConnection.ontrack = (event) => {
                        event.streams.forEach((stream) => {
                            stream.getTracks().forEach((track) => {
                                if (!mediaStream.getTracks().some((existingTrack) => existingTrack.id === track.id)) {
                                    mediaStream.addTrack(track);
                                }
                            });
                        });

                        if (videoNodeRef.current && videoNodeRef.current.srcObject !== mediaStream) {
                            videoNodeRef.current.srcObject = mediaStream;
                        }
                    };

                    peerConnection.onicecandidate = (event) => {
                        if (!event.candidate || cancelled) {
                            return;
                        }

                        if (!sessionUrlRef.current) {
                            queuedCandidatesRef.current.push(event.candidate);
                            return;
                        }

                        sendLocalCandidates([event.candidate]).catch((candidateError) => {
                            if (!cancelled) {
                                handlePlaybackError(candidateError);
                            }
                        });
                    };

                    peerConnection.onconnectionstatechange = () => {
                        if (cancelled) {
                            return;
                        }

                        if (peerConnection.connectionState === 'connected') {
                            reloadCountRef.current = 0;
                            setErrorMessage('');
                        }

                        if (['failed', 'closed', 'disconnected'].includes(peerConnection.connectionState)) {
                            handlePlaybackError(new Error('Peer connection closed'));
                        }
                    };

                    const offer = await peerConnection.createOffer();
                    await peerConnection.setLocalDescription(offer);
                    localDescriptionRef.current = parseLocalDescription(offer.sdp || '');

                    const offerResponse = await fetchWithSession(resolvedWhepUrl, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/sdp',
                        },
                        body: offer.sdp || '',
                    });

                    if (offerResponse.status === 201) {
                        const locationHeader = offerResponse.headers.get('Location');
                        if (!locationHeader) {
                            throw new Error('Missing WHEP session location');
                        }

                        sessionUrlRef.current = normalizeSessionUrl(locationHeader, resolvedWhepUrl);

                        const answerSdp = await offerResponse.text();
                        await peerConnection.setRemoteDescription(
                            new RTCSessionDescription({
                                type: 'answer',
                                sdp: answerSdp,
                            })
                        );

                        if (queuedCandidatesRef.current.length > 0) {
                            await sendLocalCandidates(queuedCandidatesRef.current);
                            queuedCandidatesRef.current = [];
                        }

                        onReady?.({
                            type: 'webrtc',
                            peerConnection,
                            sourceUrl: resolvedWhepUrl,
                        });

                        if (shouldAutoPlay && videoNodeRef.current) {
                            videoNodeRef.current.play().catch((playbackError) => {
                                if (debug) {
                                    console.debug('[MediaMtxWebRtcPlayer] Autoplay blocked', playbackError);
                                }
                            });
                        }

                        return;
                    }

                    lastError = new Error(`Unexpected WHEP offer response: ${offerResponse.status}`);
                    cleanupConnection(false);

                    if (offerResponse.status === 404 && attempt === 0 && live) {
                        await waitFor(STARTUP_RETRY_DELAY_MS);
                        continue;
                    }

                    throw lastError;
                }

                throw lastError || new Error('Unexpected WHEP bootstrap failure');
            } catch (error) {
                cleanupConnection(false);
                if (!cancelled) {
                    handlePlaybackError(error);
                }
            }
        };

        startPlayback();

        return () => {
            cancelled = true;
            if (retryTimerRef.current) {
                clearTimeout(retryTimerRef.current);
                retryTimerRef.current = null;
            }
            cleanupConnection(true);
        };
    }, [bootstrapLivePreview, cleanupConnection, debug, handlePlaybackError, live, onReady, reloadNonce, resolvedWhepUrl, sendLocalCandidates, shouldAutoPlay, waitFor]);

    useEffect(() => {
        const triggerKeepAlive = () => fetchWithAuth(keepAliveUrl, authToken, 'Keep alive');

        if (keepAliveUrl) {
            if (keepAliveImmediate) {
                triggerKeepAlive();
            }

            if (keepAliveInterval > 0) {
                scheduleRequest(triggerKeepAlive, keepAliveInterval, true);
            }
        }

        return clearScheduledRequests;
    }, [
        authToken,
        clearScheduledRequests,
        keepAliveImmediate,
        keepAliveInterval,
        keepAliveUrl,
        scheduleRequest,
    ]);

    /**
     * Force a fresh connection attempt from the rendered reload button.
     */
    const handleReload = useCallback(() => {
        reloadCountRef.current = 0;
        setReloadNonce((value) => value + 1);
    }, []);

    const videoStyle = useMemo(() => {
        if (fluid) {
            return {
                width: '100%',
                height: '100%',
                aspectRatio: width && height ? `${width} / ${height}` : undefined,
            };
        }

        return {
            width: width ?? '100%',
            height: height ?? 'auto',
        };
    }, [fluid, height, width]);

    const gridOverlayStyle = useMemo(() => {
        const style = {
            position: 'absolute',
            left: 0,
            top: 0,
            width: '100%',
            height: '100%',
            zIndex: 4,
            backgroundRepeat: 'repeat',
            cursor: gridEnabled ? 'pointer' : 'default',
            pointerEvents: gridEnabled ? 'auto' : 'none',
        };

        if (gridDebug && gridColumns > 0 && gridRows > 0) {
            const columnSize = `${(100 / gridColumns).toFixed(4)}%`;
            const rowSize = `${(100 / gridRows).toFixed(4)}%`;
            style.backgroundImage = `linear-gradient(to right, ${gridOverlayColor} 1px, transparent 1px), linear-gradient(to bottom, ${gridOverlayColor} 1px, transparent 1px)`;
            style.backgroundSize = `${columnSize} ${rowSize}`;
        } else {
            style.backgroundImage = 'none';
            style.backgroundSize = 'auto';
        }

        return style;
    }, [gridColumns, gridDebug, gridEnabled, gridOverlayColor, gridRows]);

    const handleGridOverlayClick = useCallback((event) => {
        if (!gridEnabled || !onGridCellClick || !videoNodeRef.current) {
            return;
        }

        const rect = resolveContainedMediaRect(videoNodeRef.current) || videoNodeRef.current.getBoundingClientRect();
        if (!rect.width || !rect.height) {
            return;
        }

        const relativeX = (event.clientX - rect.left) / rect.width;
        const relativeY = (event.clientY - rect.top) / rect.height;
        if (relativeX < 0 || relativeX > 1 || relativeY < 0 || relativeY > 1) {
            return;
        }

        event.stopPropagation();
        event.preventDefault();

        const column = Math.min(gridColumns, Math.max(1, Math.ceil(relativeX * gridColumns)));
        const row = Math.min(gridRows, Math.max(1, Math.ceil(relativeY * gridRows)));
        onGridCellClick(row, column, event);
    }, [gridColumns, gridEnabled, gridRows, onGridCellClick]);

    useEffect(() => {
        const updateFullscreenState = () => {
            const fullscreenElement = document.fullscreenElement || document.webkitFullscreenElement;
            const rootEl = rootRef.current;
            setIsBrowserFullscreen(Boolean(rootEl && fullscreenElement && rootEl.contains(fullscreenElement)));
        };

        document.addEventListener('fullscreenchange', updateFullscreenState);
        document.addEventListener('webkitfullscreenchange', updateFullscreenState);
        updateFullscreenState();

        return () => {
            document.removeEventListener('fullscreenchange', updateFullscreenState);
            document.removeEventListener('webkitfullscreenchange', updateFullscreenState);
        };
    }, []);

    useEffect(() => {
        if (!gridEnabled || !videoNodeRef.current) {
            setGridOverlayBounds(null);
            return undefined;
        }

        const updateBounds = () => {
            if (!videoNodeRef.current) {
                return;
            }

            const rect = resolveContainedMediaRect(videoNodeRef.current);
            const videoRect = videoNodeRef.current.getBoundingClientRect();
            if (!rect || !videoRect.width || !videoRect.height) {
                setGridOverlayBounds(null);
                return;
            }

            setGridOverlayBounds({
                left: Math.max(0, rect.left - videoRect.left),
                top: Math.max(0, rect.top - videoRect.top),
                width: rect.width,
                height: rect.height,
            });
        };

        updateBounds();
        const videoEl = videoNodeRef.current;
        videoEl.addEventListener('loadedmetadata', updateBounds);
        window.addEventListener('resize', updateBounds);
        document.addEventListener('fullscreenchange', updateBounds);

        return () => {
            videoEl.removeEventListener('loadedmetadata', updateBounds);
            window.removeEventListener('resize', updateBounds);
            document.removeEventListener('fullscreenchange', updateBounds);
        };
    }, [gridEnabled, reloadNonce, sourceUrl]);

    return (
        <div ref={rootRef} className={rootClassName} style={{ position: 'relative' }}>
            <div className="live-video-player__surface" style={{ position: 'relative' }}>
                {normalizedOverlayActions.length > 0 ? (
                    <div className={actionsContainerClassName}>
                        {normalizedOverlayActions.map((action, index) => {
                            const {
                                key,
                                label,
                                icon,
                                hint,
                                buttonClassName,
                                onClick: actionHandler,
                                disabled,
                                title,
                            } = action;

                            const resolvedKey = key ?? `overlay-action-${index}`;
                            const resolvedButtonClass = buttonClassName || 'btn btn-sm btn-outline-secondary';

                            return (
                                <React.Fragment key={resolvedKey}>
                                    <button
                                        type="button"
                                        className={resolvedButtonClass}
                                        onClick={(event) => {
                                            if (typeof actionHandler === 'function') {
                                                actionHandler(event);
                                            }
                                        }}
                                        disabled={Boolean(disabled)}
                                        title={title || label}
                                    >
                                        {icon ? <i className="material-icons mr-1">{icon}</i> : null}
                                        {label ?? ''}
                                    </button>
                                    {hint ? <span className="live-video-player__actions-hint">{hint}</span> : null}
                                </React.Fragment>
                            );
                        })}
                    </div>
                ) : null}
                <video
                    id={resolvedId}
                    ref={videoNodeRef}
                    className={className}
                    playsInline={playsInline}
                    muted={muted}
                    loop={loop}
                    controls={controls}
                    autoPlay={shouldAutoPlay}
                    preload={resolvedPreload}
                    style={videoStyle}
                />
                <div
                    className={gridOverlayClassName}
                    style={{
                        ...gridOverlayStyle,
                        left: gridOverlayBounds ? `${gridOverlayBounds.left}px` : gridOverlayStyle.left,
                        top: gridOverlayBounds ? `${gridOverlayBounds.top}px` : gridOverlayStyle.top,
                        width: gridOverlayBounds ? `${gridOverlayBounds.width}px` : gridOverlayStyle.width,
                        height: gridOverlayBounds ? `${gridOverlayBounds.height}px` : gridOverlayStyle.height,
                    }}
                    onClick={handleGridOverlayClick}
                    aria-hidden="true"
                />
            </div>
            {errorMessage ? (
                <div className="alert alert-warning mt-2 mb-0" role="alert">
                    {errorMessage}
                </div>
            ) : null}
            {!isBrowserFullscreen && (allowReload || normalizedFooterActions.length > 0) ? (
                <div className="live-video-player__footer d-flex flex-wrap align-items-center" style={{ gap: '0.5rem' }}>
                    {allowReload && showReloadButton ? (
                        <button type="button" className={reloadButtonClassName} onClick={handleReload}>
                            {reloadButtonText}
                        </button>
                    ) : null}
                    {normalizedFooterActions.map((action, index) => {
                        const resolvedKey = action.key ?? `footer-action-${index}`;
                        const resolvedButtonClass = action.buttonClassName || 'btn btn-sm btn-outline-secondary mt-3';

                        return (
                            <button
                                key={resolvedKey}
                                type="button"
                                className={resolvedButtonClass}
                                onClick={(event) => {
                                    if (typeof action.onClick === 'function') {
                                        action.onClick(event);
                                    }
                                }}
                                disabled={Boolean(action.disabled)}
                                title={action.title || action.label}
                            >
                                {action.icon ? <i className="material-icons mr-1">{action.icon}</i> : null}
                                {action.label ?? ''}
                            </button>
                        );
                    })}
                </div>
            ) : null}
        </div>
    );
};

MediaMtxWebRtcPlayer.propTypes = {
    playerId: PropTypes.string,
    className: PropTypes.string,
    wrapperClassName: PropTypes.string,
    sourceUrl: PropTypes.string,
    controls: PropTypes.bool,
    autoplay: PropTypes.bool,
    muted: PropTypes.bool,
    loop: PropTypes.bool,
    preload: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    fluid: PropTypes.bool,
    height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    live: PropTypes.bool,
    playsInline: PropTypes.bool,
    allowReload: PropTypes.bool,
    showReloadButton: PropTypes.bool,
    reloadButtonClassName: PropTypes.string,
    reloadButtonText: PropTypes.string,
    reloadOnError: PropTypes.bool,
    reloadLimit: PropTypes.number,
    reloadDelayMs: PropTypes.number,
    onlineStatusUrl: PropTypes.string,
    onlineStatusImmediate: PropTypes.bool,
    onlineStatusInterval: PropTypes.number,
    onlineStatusInitialDelay: PropTypes.number,
    keepAliveUrl: PropTypes.string,
    keepAliveImmediate: PropTypes.bool,
    keepAliveInterval: PropTypes.number,
    authToken: PropTypes.string,
    debug: PropTypes.bool,
    gridColumns: PropTypes.number,
    gridRows: PropTypes.number,
    gridDebug: PropTypes.bool,
    gridOverlayClassName: PropTypes.string,
    gridOverlayColor: PropTypes.string,
    onGridCellClick: PropTypes.func,
    overlayActions: PropTypes.arrayOf(
        PropTypes.shape({
            key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            label: PropTypes.string,
            icon: PropTypes.string,
            hint: PropTypes.string,
            buttonClassName: PropTypes.string,
            onClick: PropTypes.func,
            disabled: PropTypes.bool,
            title: PropTypes.string,
        })
    ),
    overlayVisible: PropTypes.bool,
    overlayClassName: PropTypes.string,
    footerActions: PropTypes.arrayOf(
        PropTypes.shape({
            key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            label: PropTypes.string,
            icon: PropTypes.string,
            buttonClassName: PropTypes.string,
            onClick: PropTypes.func,
            disabled: PropTypes.bool,
            title: PropTypes.string,
        })
    ),
    onReady: PropTypes.func,
    onError: PropTypes.func,
};

export default MediaMtxWebRtcPlayer;
